/**
 * Router plugin: emits page_view and page_exit for SPA navigation.
 */
import type { PluginContext } from "../lib/types";
import { now } from "../lib/utils";

import { registerPlugin } from "./register";

export function createRouterPlugin(ctx: PluginContext) {
  let currentUrl = window.location.href;
  let lastChangeAt = now();
  let active = false;
  let originalPushState: History["pushState"] | undefined;
  let originalReplaceState: History["replaceState"] | undefined;
  let installedPushState: History["pushState"] | undefined;
  let installedReplaceState: History["replaceState"] | undefined;

  const emitPageView = (url: string, referrer?: string) => {
    if (!ctx.canTrackCategory("behavioral_events")) return;
    ctx.track("page_view", {
      url,
      referrer,
      title: document.title,
      path: window.location.pathname,
    });
  };

  const emitPageExit = (url: string) => {
    if (!ctx.canTrackCategory("behavioral_events")) return;
    const durationMs = now() - lastChangeAt;
    ctx.track("page_exit", {
      url,
      duration_ms: durationMs,
    });
  };

  const handleChange = () => {
    if (!active) return;
    const nextUrl = window.location.href;
    if (nextUrl === currentUrl) return;
    emitPageExit(currentUrl);
    const referrer = currentUrl;
    currentUrl = nextUrl;
    lastChangeAt = now();
    emitPageView(nextUrl, referrer);
  };

  const patchHistory = () => {
    originalPushState = history.pushState;
    originalReplaceState = history.replaceState;

    installedPushState = function (this: History, ...args: any[]) {
      originalPushState!.apply(this, args as any);
      window.dispatchEvent(new Event("prospix:routechange"));
    };

    installedReplaceState = function (this: History, ...args: any[]) {
      originalReplaceState!.apply(this, args as any);
      window.dispatchEvent(new Event("prospix:routechange"));
    };
    history.pushState = installedPushState;
    history.replaceState = installedReplaceState;

    window.addEventListener("popstate", handleChange);
    window.addEventListener("prospix:routechange", handleChange);
  };

  const unpatchHistory = () => {
    window.removeEventListener("popstate", handleChange);
    window.removeEventListener("prospix:routechange", handleChange);
    if (installedPushState && history.pushState === installedPushState) {
      history.pushState = originalPushState!;
    }
    if (
      installedReplaceState &&
      history.replaceState === installedReplaceState
    ) {
      history.replaceState = originalReplaceState!;
    }
    originalPushState = undefined;
    originalReplaceState = undefined;
    installedPushState = undefined;
    installedReplaceState = undefined;
  };

  return {
    onInit() {
      if (active) return;
      active = true;
      currentUrl = window.location.href;
      lastChangeAt = now();
      emitPageView(currentUrl, document.referrer || undefined);
      patchHistory();
    },
    onUnload() {
      if (!active) return;
      emitPageExit(currentUrl);
      active = false;
      unpatchHistory();
    },
  };
}

registerPlugin("router", createRouterPlugin);
